import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { HasOnComplete, HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { ReorderResult } from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Search from "@insite/mobius/Icons/Search";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENOrderSummaryCardTable from "@tCentro/Components/CENOrderSummaryCardTable";
import CENRecentOrderCardList from "@tCentro/Components/CENRecentOrderCardList";
import CENRecentOrdersCardListHeading from "@tCentro/Components/CENRecentOrdersCardListHeading";
import { getDataLayerUtilityV4 } from "@tCentro/Custom/Utilities/getDataLayerUtility";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        [fields.backgroundColor]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        ordersDataViewUpdated: getOrdersDataView(state, recentOrdersParameter),

        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        language: state.context.session.language,
        isReordering: state.pages.orderHistory.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
    };
};

const mapDispatchToProps = {
    loadOrders,
    reorder,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    searchfieldStyles?: TextFieldPresentationProps;
    orderNumberHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderDateHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    dataTable?: DataTableProps;
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    statusHeader: {
        css: css`
            width: 50%;
        `,
    },
    noOrdersFoundText: {
        css: css`
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            text-align: center;
            color: ${getProp("theme.typography.p.color")};
        `,
    },

    spinner: {
        css: css`
            margin: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    container: {
        css: css`
            // margin-top: 32px;
        `,
    },
    dataTable: {
        css: css`
            th {
                background: none;
                span {
                    font-family: Poppins;
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 16px;
                    text-align: left;
                    color: ${getProp("theme.typography.p.color")};
                }
            }

            tbody {
                tr {
                    &:nth-child(odd) {
                        background: #f2f2f2;
                    }
                    &:nth-child(even) {
                        background: #ffffff;
                    }
                }
            }

            td {
                border-bottom: none;
                span {
                    font-family: Poppins;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 16px;
                    text-align: left;
                    color: ${getProp("theme.typography.p.color")};
                }
            }
        `,
    },
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: {
        width: 12,
        css: css`
            background-color: #f2f2f2;
            margin: 32px 0px !important;
            padding: 66px 24px 66px 24px;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    searchfieldStyles: {
        iconProps: {
            src: Search,
        },

        cssOverrides: {
            formField: css`
                margin-top: 32px;
            `,
            inputSelect: css`
                border: 2px solid #f2f2f2;
                padding: 12px 24px 12px 24px;
                font-family: Poppins;
                font-size: 14px;
                font-weight: 400;
                line-height: 24px;
                text-align: left;
                border-radius: 40px;
            `,
        },
    },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC",
};

const styles = recentOrdersStyles;
type StateCustom = {
    parentCheckBox: boolean;
};

class RecentOrders extends React.Component<Props, StateCustom> {
    constructor(props: Props) {
        super(props);

        this.state = {
            parentCheckBox: false,
        };
    }
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    updateState = (newState: boolean) => {
        this.setState({ parentCheckBox: newState });
    };

    componentDidUpdate(prevProps: Props, prevState: StateCustom) {
        if (prevState.parentCheckBox !== this.state.parentCheckBox) {
            const { parentCheckBox } = this.state;
            const { loadOrders } = this.props;

            if (parentCheckBox) {
                recentOrdersParameter.status = ["1", "2"];
                loadOrders(recentOrdersParameter);
            } else {
                recentOrdersParameter.status = [];
                loadOrders(recentOrdersParameter);
            }
        }
    }

    render() {
        const {
            ordersDataView,
            orderHistoryPageLink,
            settingsCollection,
            canViewOrders,
            isReordering,
            isUpdatingCart,
            language,
            isVmiOrderHistoryPage,
            fields,
        } = this.props;

        // if (!ordersDataView.value) {
        //     return null;
        // }
        // if (!canViewOrders || !ordersDataView.value) {
        //     return null;
        // }

        const orderHistoryUrl =
            orderHistoryPageLink && ordersDataView?.value && ordersDataView?.value?.length > 0
                ? orderHistoryPageLink.url
                : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        const rows = ordersDataView?.value?.map(order => {
            return {
                id: order.id,
                linkOrderNumber: order.webOrderNumber || order.erpOrderNumber,
                date: order.orderDate
                    ? getLocalizedDateTime({
                        dateTime: new Date(order.orderDate),
                        language: this.props.language,
                        options: {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                        },
                      })
                    : "",
                orderNumber: order.webOrderNumber || order.erpOrderNumber,
                status: order.statusDisplay,
                po: order.customerPO,
                total: order.orderGrandTotalDisplay,
                isVmiOrder: !!order.vmiLocationId,
                firstProductImage: order.properties.firstProductImage,
                firstProductUri: order.properties.firstProductUri,
                remainingProductCount: order.properties.remainingProductCount,
                properties: order.properties,
            };
        });
        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper
                    css={css`
                        height: 16px;
                        background: ${this.props.fields.backgroundColor};
                    `}
                ></StyledWrapper>

                <StyledWrapper
                    css={css`
                        border: 4px solid #f2f2f2;
                        height: 100%;
                    `}
                >
                    <CENRecentOrdersCardListHeading
                        heading={translate("Recent Orders")}
                        viewAllUrl={orderHistoryUrl}
                        ordersDataView={ordersDataView}
                        updateTheState={this.updateState}
                        parentCheckBox={this.state.parentCheckBox}
                    />
                    <CENRecentOrderCardList data-test-selector="widget_RecentOrders">
                        {ordersDataView.isLoading ? (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        ) : (
                            <>
                                {ordersDataView.value && ordersDataView.value.length === 0 && (
                                    <GridItem className="no-orders-found-grid-item" {...styles.noOrdersFoundGridItem}>
                                        <Typography
                                            className="no-orders-found-text"
                                            {...styles.noOrdersFoundText}
                                            data-test-selector="recentOrders_NoOrdersFound"
                                        >
                                            {translate("No orders found")}
                                        </Typography>
                                    </GridItem>
                                )}
                                {ordersDataView.value && ordersDataView.value.length > 0 && (
                                    <StyledWrapper {...styles.container}>
                                        <DataTable {...styles.dataTable}>
                                            <DataTableHead>
                                                <DataTableHeader
                                                    {...styles.customerPOHeader}
                                                    title={translate("Items Purchased")}
                                                >
                                                    {translate("Items Purchased", "customerPO")}
                                                </DataTableHeader>

                                                <DataTableHeader
                                                    {...styles.customerPOHeader}
                                                    title={translate("Purchase Order Number")}
                                                >
                                                    {translate("PO #", "customerPO")}
                                                </DataTableHeader>

                                                <DataTableHeader
                                                    {...styles.orderNumberHeader}
                                                    title={translate("Order Number")}
                                                    data-test-selector="tableHeader_webOrderNumber"
                                                >
                                                    {translate("Order #", "webOrderNumber")}
                                                </DataTableHeader>

                                                <DataTableHeader
                                                    {...styles.orderDateHeader}
                                                    data-test-selector="tableHeader_OrderDate"
                                                >
                                                    {translate("Order Date", "orderDate")}
                                                </DataTableHeader>

                                                <DataTableHeader {...styles.orderTotalHeader}>
                                                    {translate("Order Total", "orderTotal")}
                                                </DataTableHeader>

                                                <DataTableHeader
                                                    {...styles.statusHeader}
                                                    data-test-selector="tableHeader_status"
                                                >
                                                    {translate("Status", "status")}
                                                </DataTableHeader>
                                            </DataTableHead>
                                            <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                                                {rows &&
                                                    rows.map(
                                                        ({
                                                            id,
                                                            linkOrderNumber,
                                                            date,
                                                            orderNumber,
                                                            status,
                                                            po,
                                                            total,
                                                            isVmiOrder,
                                                            firstProductImage,
                                                            firstProductUri,
                                                            remainingProductCount,
                                                            properties
                                                        }) => {
                                                            const onClickHandler = () => {

                                                                const price = parseFloat(properties?.firstProductPrice || "0").toFixed(2);
                                                                const value = parseFloat(properties?.firstProductSubTotal || "0").toFixed(2);

                                                                const dataLayerObject = {
                                                                    "item_id": properties?.firstProductMPN || "",
                                                                    "item_name": properties?.firstProductTitle || "",
                                                                    "item_brand": properties?.firstProductBrand || "",
                                                                    "price": Number(price),
                                                                    "quantity": Number(properties?.firstProductQtyOrdered || "1"),
                                                                    "value": Number(value),
                                                                    "item_list_id": "my_account_order_history",
                                                                    "item_list_name": "My Account: Order History"
                                                                }
                                                                dataLayerObject && getDataLayerUtilityV4(dataLayerObject)
                                                            }
                                                            return (
                                                                <CENOrderSummaryCardTable
                                                                    key={id}
                                                                    linkOrderNumber={linkOrderNumber}
                                                                    date={date}
                                                                    orderNumber={orderNumber}
                                                                    status={status}
                                                                    po={po}
                                                                    total={total}
                                                                    isReordering={isReordering}
                                                                    isUpdatingCart={isUpdatingCart}
                                                                    isVmiOrder={isVmiOrder}
                                                                    isVmiOrderHistoryPage={isVmiOrderHistoryPage}
                                                                    language={language}
                                                                    firstProductImage={firstProductImage}
                                                                    firstProductUri={firstProductUri}
                                                                    remainingProductCount={remainingProductCount}
                                                                    onClick={onClickHandler}
                                                                />
                                                            )
                                                        },
                                                    )}
                                            </DataTableBody>
                                        </DataTable>
                                    </StyledWrapper>
                                )}
                            </>
                        )}
                    </CENRecentOrderCardList>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: fields.backgroundColor,
                editorTemplate: "TextField",
                defaultValue: "linear-gradient(90deg,#266b40 27.08%,#2d804c 71.87%,#43687a 100%)",
                fieldType: "General",
                displayName: "Background Color",
            },
        ],
    },
};

export default widgetModule;
